import {
  canAddAccount,
  connectorDescriptionKey,
  connectorDisplayName,
  showsAccountList,
  supportsMultipleAccounts,
  type ConnectorStatus,
} from '../../../../lib/connectors';
import { useTranslation } from '../../../../lib/i18n';
import { connectorsTargetElementId } from '../../../../lib/connectors-route';
import { useProductName } from '../../../../lib/use-brand';
import { ConnectorLogo } from '../ConnectorLogo';
import { AccountAvatar } from '../AccountAvatar';
import { PlusIcon, TrashIcon, UnlinkIcon } from '../icons';
import { Spinner } from '../../../Spinner';
import { Badge, Button, Card, IconButton, TextButton } from '../../ui';
import styles from './mobile.module.css';
import { shopifyHealthLabelKey } from '../../../../lib/shopify-health';
import { ShopifyConnectionHealth } from '../ShopifyConnectionHealth';

/** Grey rounded logo tile: 28px, radius 8, Surface/Standard Bg (#f3f3f3) — Figma 13131-78250. */
const LOGO_TILE = 'var(--connector-plate)';
const LOGO_SIZE = 28;
const LOGO_RADIUS = 8;
// Native pins 18 on the 28dp compact tile (Figma), rather than the 0.6 default.
const LOGO_GLYPH = 18;

function useConnectorDescription(connector: ConnectorStatus): string | undefined {
  const { t } = useTranslation();
  const productName = useProductName();
  const key = connectorDescriptionKey(connector);
  if (key) {
    const localized = t(key, { productName });
    if (localized && localized !== key) return localized;
  }
  return connector.description || undefined;
}

/**
 * A connected tool card, mobile (Figma 13738-149971 "Symphony Card Box"): a 28px grey logo tile, the
 * connector name over a "N accounts connected" summary (or its description), and a top-aligned green
 * "Connected" badge, then — no "Manage" toggle — the per-account list (avatar, email over its optional
 * workspace line, and a trash button that disconnects that one account) followed by an "Add account"
 * text button. Mirrors the desktop `ConnectorCard` connected state. A connector with no listable
 * account keeps a trailing unlink button as its only disconnect.
 *
 * `compact` is the collapsed peek in the Connected fan: just the header (logo, name, summary, badge),
 * with the account list / actions withheld until "View All" flattens the deck — otherwise the tall
 * expanded cards spill past the fan's fixed peek geometry.
 */
export function ConnectedConnectorCard({
  connector,
  busy,
  multiAccountEnabled,
  compact = false,
  onDisconnect,
  onAddAnother,
  onConnect,
}: {
  connector: ConnectorStatus;
  /** The keys with an action in flight — the row asks only about its own toolkit and its own accounts.
   *  See `ConnectConnector.busy`. */
  busy: ReadonlySet<string>;
  multiAccountEnabled: boolean;
  compact?: boolean;
  onDisconnect: (connector: ConnectorStatus, name: string, accountId?: string) => void;
  onAddAnother: (connector: ConnectorStatus) => void;
  onConnect: (connector: ConnectorStatus) => void;
}) {
  const { t } = useTranslation();
  const name = connectorDisplayName(connector);
  const description = useConnectorDescription(connector);
  // `loading` already disables the DS control, so each button is inert for its own action alone.
  const connectBusy = busy.has(connector.toolkit);
  const accounts = connector.accounts ?? [];
  // The multi-account rollout (controller.state.multiAccountEnabled) gates the per-account UI and the
  // "Add account" action: authorizeConnector's `allowMultiple` is only honored server-side when the
  // rollout is on, so a disabled caller must never reach it. Off → the card falls back to a header with
  // a single unlink disconnect (below), never the account list.
  const multiAccount = multiAccountEnabled && supportsMultipleAccounts(connector);
  const isMultiAccount = multiAccount && accounts.length > 1;
  // Native's rule (see `showsAccountList`), plus the `compact` peek that withholds the list entirely and
  // the same "there has to be an account to list" guard the desktop card applies.
  const showAccountList = !compact && showsAccountList(connector, multiAccountEnabled) && accounts.length > 0;
  const isAppsScript = connector.toolkit === 'google-apps-script';
  const canAddAnotherAccount = !isAppsScript && canAddAccount(accounts.length, connector.maxAccounts);
  const secondary = isMultiAccount ? t('connectors.accounts.summary', { count: accounts.length }) : description;
  // Same rule as the desktop card: a refused authorization still has a row, so the badge has to say
  // which kind of "connected" this is, and offer the one action that fixes it.
  const needsReconnect = connector.needsReconnect === true;

  return (
    <Card id={connector.toolkit === 'shopify-direct' ? connectorsTargetElementId('tools', connector.toolkit) : undefined} className={`${styles.connectorCard} ${styles.cardShadow}`}>
      <div className={styles.cardRow}>
        <div className={styles.cardLeft}>
          <ConnectorLogo connector={connector} name={name} size={LOGO_SIZE} tileBackground={LOGO_TILE} radius={LOGO_RADIUS} glyphSize={LOGO_GLYPH} />
          <div className={styles.cardNameGroup}>
            <div className={styles.rowName}>{name}</div>
            {secondary && <div className={styles.rowDesc}>{secondary}</div>}
          </div>
        </div>
        <div className={styles.cardHeadEnd}>
          {needsReconnect ? (
            <Badge variant="waiting-on-you" label={t('connectors.reconnectNeeded')} className={styles.rowBadge} />
          ) : connector.shopifyHealth && connector.shopifyHealth.state !== 'connected' ? (
            <Badge variant="waiting-on-you" label={t(shopifyHealthLabelKey(connector.shopifyHealth))} className={styles.rowBadge} />
          ) : (
            <Badge variant="connected" label={t('common.connected')} className={styles.rowBadge} />
          )}
          {!compact && !showAccountList && (
            <IconButton
              size="tiny"
              onClick={() => onDisconnect(connector, name)}
              loading={connectBusy}
              title={t('common.disconnect')}
              aria-label={t('common.disconnect')}
            >
              <UnlinkIcon />
            </IconButton>
          )}
        </div>
      </div>

      {!compact && connector.shopifyHealth && <ShopifyConnectionHealth health={connector.shopifyHealth} />}

      {(needsReconnect || isAppsScript) && !compact && (
        <Button
          variant="outlined"
          size="small"
          onClick={() => onConnect(connector)}
          loading={connectBusy}
          className={styles.reconnect}
        >
          {connectBusy ? t('common.connecting') : isAppsScript ? t('appsScript.setup.title') : t('common.reconnect')}
        </Button>
      )}

      {showAccountList && (
        <div className={styles.accountList}>
          {/* Full-width hairline under the header (Figma 13738-149971). */}
          <div className={styles.divider} aria-hidden />
          {/* The account rows + "Add account" are indented to sit under the connector NAME (not the
              logo), while the divider above spans the whole card — see `.accountItems`. */}
          <div className={styles.accountItems}>
            {accounts.map((account, index) => {
              const label = account.label || t('connectors.accountFallback', { number: index + 1 }) || '';
              return (
                <div key={account.id} className={styles.accountRow}>
                  <AccountAvatar label={label} />
                  <div className={styles.accountText}>
                    <span className={styles.accountLabel}>{label}</span>
                    {/* The optional human/workspace line ("Work, Pizza Milano" / "Personal") — only some
                        connectors carry it, so it renders only when present. */}
                    {account.secondaryLabel && <span className={styles.accountSub}>{account.secondaryLabel}</span>}
                  </div>
                  <IconButton
                    size="tiny"
                    onClick={() => onDisconnect(connector, account.label || name, account.id)}
                    loading={busy.has(account.id)}
                    aria-label={t('connectors.mcp.removeNamed', { name: label })}
                    title={t('common.disconnect')}
                  >
                    <TrashIcon />
                  </IconButton>
                </div>
              );
            })}
            {canAddAnotherAccount && (
              <TextButton
                size="tiny"
                className={styles.addAccount}
                onClick={() => onAddAnother(connector)}
                disabled={connectBusy}
                startIcon={connectBusy ? <Spinner /> : <PlusIcon size={16} />}
              >
                {t('connectors.addAccount')}
              </TextButton>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

/**
 * An available (not-yet-connected) tool card, mobile: 28px grey logo tile, 14/18 name + description,
 * and the secondary (outlined) Connect button. `onConnect` is the shared controller's connect flow.
 */
export function AvailableConnectCard({
  connector,
  busy,
  onConnect,
}: {
  connector: ConnectorStatus;
  /** See `ConnectConnector.busy` — this card asks only about its own toolkit. */
  busy: ReadonlySet<string>;
  onConnect: (connector: ConnectorStatus) => void;
}) {
  const { t } = useTranslation();
  const name = connectorDisplayName(connector);
  const description = useConnectorDescription(connector);
  const connectBusy = busy.has(connector.toolkit);

  return (
    <Card id={connector.toolkit === 'shopify-direct' ? connectorsTargetElementId('tools', connector.toolkit) : undefined} className={`${styles.connectorCard} ${styles.cardShadow}`}>
      <div className={`${styles.cardRow} ${styles.cardRowCenter}`}>
        <div className={styles.cardLeft}>
          <ConnectorLogo connector={connector} name={name} size={LOGO_SIZE} tileBackground={LOGO_TILE} radius={LOGO_RADIUS} glyphSize={LOGO_GLYPH} />
          <div className={styles.cardNameGroup}>
            <div className={styles.rowName}>{name}</div>
            {description && <div className={styles.rowDesc}>{description}</div>}
          </div>
        </div>
        <Button
          variant="outlined"
          size="small"
          onClick={() => onConnect(connector)}
          loading={connectBusy}
          className={styles.connectBtn}
        >
          {connectBusy ? t('common.connecting') : t('common.connect')}
        </Button>
      </div>
    </Card>
  );
}
